import React, { ReactNode, useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { ArrowForward } from '@mui/icons-material'
import { Layout } from '../../components/Layout'
import { LayoutContent } from '../../components/LayoutContent'
import { Body1, H4Bold, H6Bold } from '../../components/Typography'
import { LOCALSTORAGE_KEY_SHOW_EMIS_TOUR_NO_DELAY } from '../../components/tour/tourConstants'
import { LOCALSTORAGE_KEY_SHOW_RECAT_INTRO } from '../../components/onboard/RecatOnboardSnack'
import { Flex, InnerBoxText, InnerInnerBox } from '../../components/Flex'
import { SideColumn } from '../../components/TableOfContent'
import { ButtonLightBorder } from '../../components/Buttons'
import { spacing } from '../../theme'
import {
  Feature,
  Role,
  TourId,
  useMeQuery,
  useUpdateUserTourMutation,
} from '../../graphql/generated'
import { AppDataContext } from '../../context/AppDataContext'
import { SnackAlert } from '../../components/SnackAlert'
import { Link } from '../../components/Link'
import { captureException } from '../../utils/Sentry'

// Disable "useless fragment" since each "answer" in `FaqTexts` below must be a (string or) single ReactNode, which means
// the framgents are necessary when grouping multiple paragraphs, but not for a single paragraph.
// So, to prevent having to alternate between defining a fragment or not when changing the number of paragraphs,
// the rule is disabled for this file.
/* eslint-disable react/jsx-no-useless-fragment */

// TODO: Move FAQ texts and categories to a database.

// TODO: Refactor this component into something less messy.

// TODO: Accessibility, such as tab navigation.
// Eg: https://www.w3.org/TR/2017/NOTE-wai-aria-practices-1.1-20171214/examples/dialog-modal/js/dialog.js

const MailtoLink = styled('a')`
  display: flex;
  color: black;
  text-decoration-color: black;
`

const Foo = styled('div')`
  width: 100%;
`

const LinkList = styled('ul')`
  list-style: none;
  padding-left: 0;
  margin: 0;
`

const LinkListItem = styled('li')`
  &:not(:first-child) {
    padding-top: ${spacing.small};
  }
`

export function HelpPage(): React.JSX.Element {
  const history = useHistory()
  const [startTourErrorMsg, setStartTourErrorMsg] = useState<
    string | undefined
  >()
  const [updateUserTourMutation] = useUpdateUserTourMutation()
  const { organisation, orgHasInitializedData } = useContext(AppDataContext)
  const { data } = useMeQuery() // this is called inside AppDataContext which will handle the error
  const user = data?.me

  const showRecatIntroButton =
    organisation.profileCreated &&
    organisation.enabledFeatures?.features?.includes(Feature.Transactions) &&
    user?.role &&
    [Role.SysAdmin, Role.Admin].includes(user.role)

  return (
    <>
      <SnackAlert
        open={!!startTourErrorMsg?.length}
        severity='warning'
        onClose={() => setStartTourErrorMsg(undefined)}
      >
        <Flex style={{ gap: spacing.medium }}>
          <Body1>{startTourErrorMsg}</Body1>
        </Flex>
      </SnackAlert>

      <Layout title='Hjälp'>
        <Flex
          row
          stretchWidth
          style={{ paddingTop: spacing.large, gap: spacing.large }}
        >
          <SideColumn style={{ gap: spacing.xl, minWidth: '10rem' }}>
            <InnerInnerBox column>
              <H6Bold>Rundturer</H6Bold>
              <InnerBoxText column>
                <ButtonLightBorder
                  // Can't start the tour unless there are transactions in the database.
                  disabled={!orgHasInitializedData}
                  endIcon={<ArrowForward />}
                  onClick={() => {
                    // Set flag in local storage to indicate that the tour should
                    // be started without any timer-based delay.
                    localStorage.setItem(
                      LOCALSTORAGE_KEY_SHOW_EMIS_TOUR_NO_DELAY,
                      'true',
                    )
                    updateUserTourMutation({
                      variables: {
                        tourID: TourId.EmissionTour,
                        seen: false,
                      },
                    })
                      .then(() => {
                        // The mutation updates the local cache, when it has completed
                        // the emissions page will display the tour again.
                        history.push('/emission')
                      })
                      .catch((e) => {
                        setStartTourErrorMsg(
                          'Kunde inte starta rundturen för Utsläpp',
                        )
                        captureException(e)
                      })
                  }}
                >
                  Utsläpp
                </ButtonLightBorder>
                {/* Only display if recat is available to this user */}
                {showRecatIntroButton && (
                  <ButtonLightBorder
                    // Can't start the tour unless there are transactions in the database.
                    disabled={!orgHasInitializedData}
                    endIcon={<ArrowForward />}
                    onClick={() => {
                      localStorage.setItem(
                        LOCALSTORAGE_KEY_SHOW_RECAT_INTRO,
                        'true',
                      )
                      history.push('/recat')
                    }}
                  >
                    Transaktioner
                  </ButtonLightBorder>
                )}
              </InnerBoxText>
            </InnerInnerBox>

            <Flex column style={{ gap: spacing.large }}>
              <H6Bold>Kontakt</H6Bold>
              <LinkList>
                <LinkListItem>
                  <MailtoLink href='mailto:support@svalna.se'>
                    support@svalna.se
                  </MailtoLink>
                </LinkListItem>
                <LinkListItem>
                  <MailtoLink href='mailto:info@svalna.se'>
                    info@svalna.se
                  </MailtoLink>
                </LinkListItem>
              </LinkList>
            </Flex>
            <Flex column style={{ gap: spacing.large }}>
              <H6Bold>Läs mer</H6Bold>
              <LinkList>
                <LinkListItem>
                  <Link
                    href='https://www.svalna.se/web/sv/calculations'
                    target='_blank'
                  >
                    Beräkningsmetodik
                  </Link>
                </LinkListItem>
                <LinkListItem>
                  <Link href='/user-agreement' target='_blank'>
                    Användarvillkor
                  </Link>
                </LinkListItem>
                <LinkListItem>
                  <Link href='/privacy-policy' target='_blank'>
                    Integritetspolicy
                  </Link>
                </LinkListItem>
                <LinkListItem>
                  <Link href='https://www.svalna.se'>Svalna.se</Link>
                </LinkListItem>
              </LinkList>
            </Flex>
          </SideColumn>
          <LayoutContent>
            <FAQModal />
          </LayoutContent>
          {/* Empty ToC to have even spacing on the left/right side of main content. */}
          <SideColumn style={{ minWidth: '10rem' }} />
        </Flex>
      </Layout>
    </>
  )
}

export function FAQModal(): React.JSX.Element {
  return (
    <Flex column style={{ gap: spacing.xl }}>
      <H4Bold>Frågor och svar</H4Bold>
      {/* TODO: Generate these from a database. */}
      <Foo>
        {FaqTexts.map(({ question, answer }) => (
          <FaqItem
            header={question}
            text={answer}
            key={question?.toLocaleString()}
          />
        ))}
      </Foo>
    </Flex>
  )
}

function FaqItem({
  header,
  text,
}: {
  header: string | ReactNode
  text: string | ReactNode
}) {
  const [showText, setShowText] = React.useState(false)
  return (
    <div
      style={{
        marginLeft: spacing.small,
        marginRight: spacing.small,
        marginTop: spacing.small,
        borderRadius: 8,
        padding: spacing.small,
        backgroundColor: 'white',
      }}
    >
      <button
        type='button'
        onClick={() => setShowText(!showText)}
        style={{
          width: '100%',
          border: 0,
          backgroundColor: 'transparent',
          cursor: 'pointer',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 18,
            fontWeight: 'bold',
          }}
        >
          <span
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
            }}
          >
            {header}
          </span>
          {/* TODO: Proper icon '+' */}
          <span>{showText ? '-' : '+'}</span>
        </div>
      </button>
      <div
        style={{
          padding: spacing.small,
          paddingTop: spacing.medium, // gap to header
          display: showText ? 'flex' : 'none',
          flexDirection: 'column',
          gap: spacing.medium, // gap between paragraphs (if any)
        }}
      >
        {showText && text}
      </div>
    </div>
  )
}

const FaqTexts: Array<{
  question: string | ReactNode
  answer: string | ReactNode
}> = [
  {
    question: `Hur har beräkningarna genomförts?`,
    answer: (
      <>
        <Body1>
          Grundprincipen är att utsläppen beräknas genom att multiplicera
          inköpsbeloppet i SEK med utsläppsintensiteten för motsvarande
          inköpskategori. Utsläppsintensiteterna som Svalna använder för att
          beräkna utsläppen grundar sig på Statistiska Centralbyråns
          företagsregister, miljöräkenskaper och SNI-systemet för svensk
          näringslivsindelning samt den senaste versionen av databasen Exiobase
          som hjälper oss att fånga alla utsläpp från import. För att beräkna
          utsläppen har vi kategoriserat alla köp i syfte att matcha dem med en
          lämplig utsläppsintensitet. Om leverantören är ett svenskt företag
          använder vi även information om det enskilda företaget för att justera
          resultaten.
        </Body1>
      </>
    ),
  },

  {
    question: `Vilken data har använts för att beräkna klimatpåverkan?`,
    answer: (
      <>
        <Body1>
          Det beror på vad din organisation och Svalna har kommit överens om,
          och vilka data som har funnits tillgängliga inom din organisation. I
          huvudsak bygger beräkningsmetoden på att använda information från
          bokföringen för att beräkna utsläppen av växthusgaser per spenderad
          krona inom olika inköpskategorier. Dataunderlaget består primärt av
          ekonomiska data, såsom konteringsbelopp, bokföringskonto och
          leverantör. Att använda ekonomiska data ger en god bild av utsläppen,
          utan risk för dubbelräkning. Svalna kan även använda data på er
          organisations bilpark samt energideklaration på de byggnader ni har
          lokaler i för att justera utsläppsberäkningarna.
        </Body1>
      </>
    ),
  },

  {
    question: `Hur beräknas utsläpp från uppvärmning?`,
    answer: (
      <>
        <Body1>
          Det beror på vad din organisation och Svalna har kommit överens och
          beroende på tillgänglighet, men i huvudsak använder Svalna information
          om era kostnader, i vissa fall ert pris per kWh, samt relevanta
          utsläppsvärden per kWh för olika bränslen (se beräkning av utsläpp
          från el nedan). För utsläpp från fjärrvärme använder Svalna
          information om utsläpp per kWh från aktuellt fjärrvärmenät utifrån
          vilken kommun era lokaler återfinns.
        </Body1>
      </>
    ),
  },

  {
    question: 'Vilka utsläpp ingår?',
    answer: (
      <>
        <Body1>
          Alla utsläpp kopplade till organisationens ekonomiska verksamhet och
          som ingår i det bokföringsunderlag som vi har fått tillgång till,
          ingår. Såväl direkta utsläpp som sker på plats (t.ex från fordon och
          maskiner), som indirekta utsläpp som sker på andra platser. Svalna
          beräknar utsläppen, oavsett var i världen de sker. Utsläppsfaktorerna
          som vi använder för att beräkna utsläppen baseras på Statistiska
          Centralbyråns miljöräkenskaper och har beräknats med hjälp av
          miljöanpassad multiregional input-outputanalys (EXIOBASE 3.9). Det är
          en metod som fångar alla utsläpp kopplat till användning av energi och
          andra fysiska insatsvaror i alla bakomvarande led: från
          råvaruproduktion, via tillverkning och transport, fram till att varan
          eller tjänsten levereras till slutkonsumenten.
        </Body1>
      </>
    ),
  },

  {
    question: `Vilka transaktioner ingår när utsläppen beräknas?`,
    answer: (
      <>
        <Body1>
          Inte alla ekonomiska transaktioner tillskrivs utsläpp. Grundprincipen
          är att utsläpp endast beräknas för transaktioner som avser inköp av
          varor och tjänster som organisationen konsumerar. Exempel på
          transaktioner som inte tillskrivs några utsläpp är löneutbetalningar,
          skatteinbetalningar, fakturor som har krediterats och inbetalningar
          till pensionssystem. Det samma gäller olika typer av överföringar
          mellan avdelningar eller verksamheter inom en organisation. Dessa
          transaktioner ses som intermediära pengaflöden som slutkonsumeras i
          senare led av privatpersoner och/eller aktörer inom offentlig
          verksamhet, och vars utsläpp skall tillskrivas slutkonsumenten.
        </Body1>
      </>
    ),
  },

  {
    question: `Hur säkra är resultaten?`,
    answer: (
      <>
        <Body1>
          Det är svårt att kvantifiera, men det finns flera källor till
          osäkerhet som är viktiga att känna till för en korrekt tolkning av
          resultaten. Svalna arbetar kontinuerligt med att minska flera källor
          till osäkerhet med hjälp av bättre data och mer detaljerade analyser,
          medan andra är betydligt svårare att göra något åt. En källa till
          osäkerhet är att de sektorer som utsläppsintensiteterna grundar sig
          på, är grova. Svårigheter att korrekt kategorisera alla inköp är en
          annan källa till osäkerhet, som hänger samman med kvalitén på
          bokföringsdatan. Som kund får du ta del av utvecklingen och när
          uppdateringar sker uppdateras även tidigare års resultat automatiskt.
        </Body1>
      </>
    ),
  },

  {
    question: 'Vad skiljer Svalnas metodik från andra miljöspendverktyg?',
    answer: (
      <>
        <Body1>
          Svalna har under flera år använt och vidareutvecklat beräkningar
          baserade på miljöanpassad multiregional input-output analys för att
          beräkna klimatpåverkan från inköp av varor och tjänster. Till skillnad
          från många andra miljöspendverktyg har vi utvecklat vår egen
          utsläppsmodell, vilket innebär att vi kan göra mer specifika och
          korrekta uppskattningar genom att samla in bakgrundsdata om varje
          leverantör och använda dessa för att justera utsläppsberäkningarna.
        </Body1>
        <Body1>
          Svalnas modell hanterar inflation och prisförändringar och omfattar
          fler kategorier, vilket möjliggör mer detaljerade uppskattningar över
          flera branscher och sektorer. Till skillnad från alla andra
          leverantörer använder vi data från Transportregistret och Boverkets
          energideklarationsregister för att kontrollera och justera våra
          beräkningar av kundens utsläpp från fordon och lokaler. Vi använder
          även en databas över utsläpp från alla lokala fjärrvärmenät.
          Sammantaget innebär det att vi kan erbjuda mer precisa
          klimatberäkningar än andra miljöspendverktyg.
        </Body1>
      </>
    ),
  },

  {
    question: `Vad skiljer Svalnas metodik från “aktivitetsbaserad” metodik?`,
    answer: (
      <>
        <Body1>
          Svalna använder en spendbaserad metodik som bygger på att beräkna
          utsläpp från kundens faktiska inköp. Denna metod fångar på ett
          enhetligt sätt utsläpp från hela leverantörskedjan, inklusive alla
          produktionsled.
        </Body1>
        <Body1>
          Aktivitetsbaserade metoder bygger ofta på livscykelanalyser (LCA) och
          fokuserar istället på att uppskatta utsläppen kopplat till material
          eller insatsvaror. Även om LCA kan ge intryck av hög precision, har de
          svårt att hantera de komplexa och globala värdekedjor som utgör dagens
          produktionssystem. 80 % av industriföretagens utsläpp är kopplade till
          deras leverantörskedja, vilket gör aktivitetsbaserade metoder mycket
          tidskrävande och svåra att genomföra för de stora mängder inköp som
          företag vanligtvis hanterar.
        </Body1>
        <Body1>
          Forskning och GHG-protokollet visar att spendbaserade beräkningar ofta
          är lika träffsäkra, samtidigt som de är enklare att uppdatera i takt
          med förändringar i leverantörskedjor och ekonomiska data. En
          aktivitetsbaserad metodik är enligt vår mening endast motiverad i
          specifika fall, exempelvis när kunden har en omfattande egen
          varuproduktion.
        </Body1>
      </>
    ),
  },

  {
    question: 'Hur beräknas utsläpp från el?',
    answer: (
      <>
        <Body1>
          Vi börjar med att uppskatta din elanvändning i kWh baserat på elpriset
          och dina kostnader. Därefter räknar vi ut utsläppen med hjälp av den
          svenska konsumtionsmixen (enligt Energimyndigheten). Det gäller även
          om du har valt 100 % förnybar el, till exempel “Grön el” eller “Bra
          Miljöval”. Anledningen är att Sverige redan har ett överskott av
          förnybar el i form av vattenkraft (se Gode et al., 2009 för ett längre
          resonemang). Att välja “grön” el ökar därför inte nödvändigtvis
          produktionen av förnybar el, och en lägre elanvändning kan istället
          bidra till att mer förnybar el kan exporteras till regioner med
          fossilbaserad elproduktion. Detta främjar den totala minskningen av
          utsläpp globalt.
        </Body1>
      </>
    ),
  },

  {
    question:
      'Vi har egna solpaneler, köper enbart specifikt sol- eller vindkraftsel - hur beräknas utsläpp då?',
    answer: (
      <>
        <Body1>
          I dessa fall är det rimligt att anta att ni genom att göra ett aktivt
          val bidrar till ökade incitament för produktion av dessa förnybara
          energikällor där det inte finns ett lika stort överskott som i fallet
          med vattenkraften. Sol och vindkraft innebär inga eller små utsläpp i
          för drift och underhåll, men kräver såklart utsläpp vid
          produktionsfasen som ska slås ut på den förväntade livslängden och vi
          använder etablerade utsläppsintensiteter för dessa energikällor.
          Eftersom de svenska konsumtionsbaserade utsläppen redan är så pass
          låga resulterar ovan energikällor inte i några markanta minskningar.
        </Body1>
      </>
    ),
  },

  {
    question: 'Hur förhåller sig Svalnas metod till GHG Protocol?',
    answer: (
      <>
        <Body1>
          Svalnas beräkningsmetod kan ses som en sofistikerad tillämpning av GHG
          Protocol, som syftar till att på ett så heltäckande, kostnadseffektivt
          och tillförlitligt sätt som möjligt beräkna utsläppen inom samtliga
          utsläppsscope. Svalnas metod är helt kompatibel med GHG Protocol för
          alla uppströms utsläpp, men i dagsläget behöver du som kund själv
          uppskatta nedströms utsläpp från pendling, eller be Svalna om hjälp
          med detta.
        </Body1>
      </>
    ),
  },

  {
    question: 'Vad baseras kategoriseringen på?',
    answer: (
      <>
        <Body1>
          Kategoriseringen bygger primärt på ett etablerat
          branschklassificeringssystem (SNI/NACE). Vi använder information om
          leverantörens registrering och vilket bokföringskonto köpet bokförts
          på. Sedan beräknas utsläppen med en utsläppsintensitet för respektive
          SNI-kod. Systemet innehåller ett stort antal underkategorier, men i
          Svalnas Carbon Intelligence System™ har vi slagit ihop dessa till sju
          större grupper (Energi, Fastigheter, Varor, Tjänster, Transport &
          resor, Mat & logi, Övrigt). Denna hopslagning syftar till att ge dig
          som kund en överblick över hur utsläppen fördelar sig och vi har
          försökt skapa en intuitiv uppdelning.
        </Body1>
      </>
    ),
  },

  {
    question: 'Vad kan jag göra för att förbättra utsläppsberäkningarna?',
    answer: (
      <>
        <Body1>
          För att minska risken för att köp felkategoriseras och skapa goda
          förutsättningar att genomföra detaljerade utsläppsberäkningar är det
          fördelaktigt om en så stor andel som möjligt av inköpen bokförs på
          ändamålsenliga och specifika bokföringskonton. Det är till exempel
          fördelaktigt om olika typer av resor bokförs på specifika
          bokföringskonton såsom “Resor med tåg”, “Resor med taxi”, osv,
          istället för att alla resor bokförs på ett och samma bokföringskonto.
          Det samma gäller inköp av energi och andra varor och tjänster.
          Generella konton av typen “Övrigt” där många olika slags varor och
          tjänster bokförs bör i möjligaste mån undvikas, då köp bokförda på den
          typen av bokföringskonton är svåra att matcha mot rätt
          utsläppsintensitet.
        </Body1>
      </>
    ),
  },

  {
    question:
      'När vi använder vår elektroniska utrustning, syns det då i kategorin varor?',
    answer: (
      <>
        <Body1>
          Nej, utsläpp från användning av elektronisk utrustning hamnar i
          kategorin energi (utsläpp från el). Kategorin varor avser utsläpp från
          inköp av olika slags fysiska varor, såsom kemikalier, datorer,
          maskiner, möbler, etc. Utsläppen avser de utsläpp som genereras genom
          hela tillverkningsprocessen inklusive brytning och framställning av
          insatsvaror (såsom metaller, plast, etc) fram tills att varorna
          levereras.
        </Body1>
      </>
    ),
  },

  {
    question:
      'Får vi lägre utsläpp om vi köper dyrare men mer miljövänliga varor av hög kvalitet som håller länge?',
    answer: (
      <>
        <Body1>
          Nej, inte direkt. Metoden kan i dagsläget bara använda information om
          vilken leverantör ni köpt varor från, på vilket bokföringskonto det
          registrerats och hur mycket pengar ni spenderat. Sedan beräknas
          utsläppen med genomsnittliga utsläppsfaktorer för varje
          inköpskategori. Det innebär att dyrare varor får högre utsläpp. Men
          indirekt bör det synas på längre sikt, om varorna är av högre
          kvalitet, eftersom ni då inte behöver köpa nytt lika ofta. Vi arbetar
          nu med en generell lösning för att också fånga detta när så möjligt.
        </Body1>
      </>
    ),
  },

  {
    question: 'Varför får vi utsläpp från konsulttjänster?',
    answer: (
      <>
        <Body1>
          Om ni köper in konsulttjänster betalar ni för alla uppströms utsläpp
          från konsultföretagets verksamhet. Även om er konsult arbetar
          “inhouse” och är i princip som en anställd så går ofta en stor del av
          kostnaden till konsultens organisation som också har lokaler, resor,
          kontorsutrustning m.m.
        </Body1>
      </>
    ),
  },

  {
    question:
      'Jag vet att en av mina leverantörer har väsentligt lägre utsläpp än er uppskattning, vad kan jag göra?',
    answer: (
      <>
        <Body1>
          Det beror på vad din organisation och Svalna har kommit överens om,
          för större företag finns möjligheten att justera och lägga in egna
          värden och Svalna kan också vara behjälpliga med att hjälpa till med
          detta mot separat avgift. Om din leverantör inte har en komplett
          uppskattning av utsläppen från den vara eller tjänst som du köpt kan
          detta även hanteras. Du kan även tipsa din leverantör om att beräkna
          sina resultat med hjälp av Svalnas metod. Om du hittat ett uppenbart
          fel i kategoriseringen, kontakta oss så hjälper vi dig.
        </Body1>
      </>
    ),
  },

  {
    question: 'Är utsläppen från vår verksamhet hög eller låg?',
    answer: (
      <>
        <Body1>
          Det beror på vad man jämför med och vad målsättningen är. Ni kan
          jämföra utsläppen per omsatt krona med andra företag i samma bransch
          eller med genomsnittet för svenska företag. Samtidigt kan lokala
          förutsättningar (t.ex. fjärrvärme med låga utsläpp) påverka
          resultaten. Svalna arbetar på att möjliggöra mer avancerade
          jämförelser, men i nuläget är systemet främst ett verktyg för att
          följa utvecklingen över tid och identifiera var åtgärder kan sättas
          in.
        </Body1>
      </>
    ),
  },

  {
    question: 'Erbjuder Svalna klimatkompensation?',
    answer: (
      <>
        <Body1>
          Nej, det gör vi i dagsläget inte. Vi vill fokusera på att hjälpa er
          minska era verksamhetsrelaterade utsläpp genom konkreta åtgärder. Vi
          fokuserar på att identifiera möjligheter att minska utsläppen i Scope
          1 och 2 genom effektiviseringar, samt att stötta er i att engagera och
          ställa krav på era leverantörer för att driva förändringsprocesser i
          hela leverantörskedjan. Klimatkompensation är förknippat med flera
          risker och bör, enligt vår mening, användas med stor försiktighet.
          Samtidigt framstår det som oundvikligt att negativa utsläpp kommer att
          krävas för att uppnå globala klimatmål och det återstår att se hur
          systemen kommer utvecklas. Svalna följer denna utveckling noggrant och
          vi kan komma att erbjuda klimatkompensation i framtiden. Vi har för
          närvarande inte stöd i vårt system för att automatiskt hantera
          negativa utsläpp. Om ni är intresserade av att tillgodoräkna er
          negativa utsläpp, är ni välkomna att kontakta oss för en diskussion om
          möjliga lösningar.
        </Body1>
      </>
    ),
  },

  {
    question: 'Vad är negativa utsläpp?',
    answer: (
      <>
        <Body1>
          Negativa utsläpp är med andra ord kolsänkor. Det finns naturliga
          kolsänkor såsom skog och hav men nu diskuteras även tekniska kolsänkor
          så som Bio-CCS och DACCS som ska fånga upp kolet/koldioxiden vid
          källan eller ur luften för att sedan förvara det under en längre
          tidsperiod. Det kommer troligtvis att krävas en viss mängd negativa
          utsläpp för att klara klimatmålen, men kostnaderna är idag höga och
          många projekt osäkra.
        </Body1>
      </>
    ),
  },

  {
    question: 'Hur avslutar jag ett konto hos er?',
    answer: (
      <>
        <Body1>Kontakta oss så löser vi det.</Body1>
      </>
    ),
  },
]
